import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ColDef, GetRowIdParams, GridOptions, IServerSideGetRowsParams, IsRowSelectable, RowNode } from "ag-grid-community";
import { ID_BILLABLE, ID_BILLED, ID_REJECTED, ID_REPLACED, ID_SENT } from "../constants/EnelxStatuses";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { STATE_RECHARGE_ACTIVE_FILTERS, STATE_RECHARGE_TOTAL_COUNT, STATE_RECHARGE_UPDATING } from "../constants/EnelxRecoil";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { useRechargeRepository } from "../../../api/repositories/RechargeRepository";
import ColouredChip from "../../../component/ColouredChip";
import { COLOR_BILLABLE, COLOR_BILLED, COLOR_REJECTED, COLOR_REPLACED, COLOR_SENT } from "../constants/EnelxColors";
import { formatDate } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { NumericSummary } from "../../../component/NumericSummary";
import EmptyState from "../../../../../util/component/EmptyState";
import { useAgGridLocale } from "../../../../../config/globalConfig";
import { Button } from "@mui/material";
import { FileDownloadRounded } from "@mui/icons-material";
import dayjs from "dayjs";
import {ExportDataAsExcelBase} from "../../../utils/ExportDataAsExcelBase";

const RechargeTable = () => {

    const UNIQUE_ID = "fnn-recharge";

    const { t } = useTranslation('finance', { keyPrefix: 'enelx' });
    const { getRecharges, getSummary } = useRechargeRepository();

    const setTotalCount = useSetRecoilState(STATE_RECHARGE_TOTAL_COUNT);
    const rechargeFilterDefs = useRecoilValue(STATE_RECHARGE_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_RECHARGE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_RECHARGE_UPDATING);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);

    const columnDefs : ColDef[] = [
        {
            field: 'reportName', headerName: t('tables.recharge.report-name'), headerTooltip: t('tables.recharge.report-name'),
            suppressSizeToFit: true,
            minWidth: 250, unSortIcon: true, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true
        },
        {
            field: 'rfidCard', headerName: t('tables.recharge.rfid-card'), headerTooltip: t('tables.recharge.rfid-card'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'sessionStart', headerName: t('tables.recharge.session'), headerTooltip: t('tables.recharge.session'),
            minWidth: 200, unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'plate', headerName: t('tables.recharge.plate'), headerTooltip: t('tables.recharge.plate'),
            minWidth: 200, unSortIcon: false, sortable: true
        },
        {
            field: 'chargingDuration', headerName: t('tables.recharge.recharge-duration'), headerTooltip: t('tables.recharge.recharge-duration'),
            minWidth: 200, unSortIcon: true, sortable : false
        },
        {
            field: 'province', headerName: t('tables.recharge.recharge-place'), headerTooltip: t('tables.recharge.recharge-place'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'useType', headerName: t('tables.recharge.station-type'), headerTooltip: t('tables.recharge.station-type'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'totalEnergy', headerName: t('tables.recharge.energy-total'), headerTooltip: t('tables.recharge.energy-total'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'customerName', headerName: t('tables.recharge.customer'), headerTooltip: t('tables.recharge.customer'),
            minWidth: 200, unSortIcon: false, sortable: false
        },
        {
            field: 'customerTaxCode', headerName: t('tables.recharge.customer-tax-code'), headerTooltip: t('tables.recharge.customer-tax-code'),
            minWidth: 200, unSortIcon: false, sortable: false
        },
        {
            field: 'raCode', headerName: t('tables.recharge.ra-code'), headerTooltip: t('tables.recharge.ra-code'),
            minWidth: 200, unSortIcon: false, sortable: false
        },
        {
            field: 'status', headerName: t('tables.recharge.status'), headerTooltip: t('tables.recharge.status'),
            minWidth: 150, unSortIcon: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                const status = params.data.status;
                switch (status) {
                    case ID_BILLABLE:
                        return {
                            component: () => <ColouredChip name={ID_BILLABLE} color={COLOR_BILLABLE}/>
                        };
                    case ID_BILLED:
                        return {
                            component: () => <ColouredChip name={ID_BILLED} color={COLOR_BILLED}/>
                        };
                    case ID_REJECTED:
                        return {
                            component: () => <ColouredChip name={ID_REJECTED} color={COLOR_REJECTED} tooltip={t(`import.rejected-reasons.${params.data.errorMessage}`)}/>
                        };
                    case ID_REPLACED:
                        return {
                            component: () => <ColouredChip name={ID_REPLACED} color={COLOR_REPLACED}/>
                        }
                    case ID_SENT:
                        return {
                            component: () => <ColouredChip name={ID_SENT} color={COLOR_SENT}/>
                        }
                    default:
                        break;
                }
            }
        },
        {
            field: "legalEntity", headerName: t("tables.report.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        },
        {
            field: "dealer", headerName: t("tables.report.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        },
    ];


    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: { title: t('tables.recharge.no-rows-overlay.title') }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(rechargeFilterDefs))
        setSummaryUpdating(true);
    }, [rechargeFilterDefs])


    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(rechargeFilterDefs));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating])

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[]) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getRecharges(startRow, endRow - startRow, sort, order, filterDefs)
                        .then(response => {
                            let result = response.result;

                            setTotalCount(result.page.totalElements);

                            if (result.page.totalElements === 0) {
                                gridRef.current!.api.showNoRowsOverlay();
                            } else {
                                gridRef.current!.api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result.page.totalElements
                            });
                        })
                        .catch(() => params.fail());
            }
        };
    }


    const isRowSelectable = useMemo<IsRowSelectable>(() => {
        return (rowNode: RowNode) => {
            let status = rowNode.data.status;
            return !(status === ID_BILLED || status === ID_SENT);
        };
    }, []);

    const toolbar = {
        renderInfo: () => (
                <NumericSummary query={() => getSummary(filterDefs)} fields={[ID_REJECTED, ID_BILLABLE, ID_SENT, ID_BILLED, ID_REPLACED]} i18nPath={"enelx.counters.recharges"}/>
        ),
        renderActions: () => (
            <>
                <Button fullWidth color={"accent"} variant="contained" component="label" onClick={exportAsExcel}>
                    <FileDownloadRounded/> {t('tables.recharge.exportRecharges')}
                </Button>
            </>
        )
    }

    function getExportableExcelColumns(): string[] {
        let keys: string[] = gridRef?.current?.columnApi.getAllDisplayedColumns().map(column => column.getColId()) as string[];
        let amountIndex: number = keys.findIndex(column => column === 'status');
        keys.splice(amountIndex + 1, 0, 'status-reason');
        return keys;
    }
    function exportAsExcel() {
        gridRef?.current?.api.exportDataAsExcel({
            ...ExportDataAsExcelBase,
            author: "Finance - Payments",
            fileName: `Recharges - ${dayjs().format("L")}.xlsx`,
            sheetName: `Recharges Enel-X`,
            columnKeys: getExportableExcelColumns(),

            processCellCallback: params => {
                if (params.column.getColId() === "status-reason") {
                    return t(`import.rejected-reasons.${params.node?.data.errorMessage}`);
                }
                return params.value;
            },
        });
    }

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api?.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    return (
            <AgGridCustomTable
                    toolbar={toolbar}
                    rowModelType='serverSide'
                    gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                    paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                    rowSelection={'multiple'} isRowSelectable={isRowSelectable}
                    noRowsOverlayComponent={noRowsOverlayComponent}
                    suppressRowClickSelection
                    onGridReady={() => {
                        let gridState = getLocalGridState(UNIQUE_ID).cols;
                        if (gridState !== undefined) {
                            gridRef.current!.columnApi.applyColumnState({
                                state: gridState,
                                applyOrder: true
                            })
                        }

                        gridRef.current!.api.sizeColumnsToFit();
                        gridRef.current!.api.setServerSideDatasource(createDatasource(rechargeFilterDefs));
                    }}
                    onColumnMoved={e => {
                        if (e.source === "api") return;
                        saveState();
                    }}
                    onColumnPinned={e => {
                        if (e.source === "api") return;
                        saveState();
                    }}
                    onColumnResized={e => {
                        if (e.source === "api") return;
                        saveState();
                    }}
                    onSortChanged={e => {
                        if (e.source === "api") return;
                        saveState();
                    }}
                    onPaginationChanged={() => {
                        setLocalGridState(UNIQUE_ID, {
                            rows: gridRef.current!.api?.paginationGetPageSize(),
                            cols: getLocalGridState(UNIQUE_ID).cols
                        });
                    }}
            />
    );
}

export default RechargeTable;
